import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Download, X, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExportDataButton() {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open || summary) return;
    setLoading(true);
    axios.get(`${API}/user/export-summary`, { withCredentials: true })
      .then((r) => setSummary(r.data))
      .catch(() => toast.error('Kunde inte hämta översikt 💔'))
      .finally(() => setLoading(false));
  }, [open, summary]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/user/export-data`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `focuscritter-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('💾 Din data är säkert nedladdad!');
      setOpen(false);
    } catch (e) {
      toast.error('Nedladdning misslyckades. Försök igen 💔');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="neo-btn px-2 py-1.5 bg-gradient-to-br from-[#B9FBC0] to-[#A0EBAA] flex items-center gap-1 text-xs font-bold"
        title="Exportera all min data som JSON (GDPR-backup)"
        data-testid="export-data-btn"
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">Exportera</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center p-4"
          onClick={() => !downloading && setOpen(false)}
          data-testid="export-data-modal"
        >
          <div
            className="bg-[#FFF9F1] neo-card max-w-md w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => !downloading && setOpen(false)}
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-black/10"
              data-testid="export-data-close"
              aria-label="Stäng"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-6 h-6 text-emerald-700" />
              <h2 className="text-xl font-bold font-['Fredoka']">Exportera min data</h2>
            </div>

            <p className="text-sm text-gray-700 mb-4">
              Ladda ner <b>allt</b> — mål, rutiner, skincare, kalender, Farmor-samtal,
              produkter, magi-framsteg, dagboksinlägg med mera — som en JSON-fil.
              Perfekt som trygg backup 💾✨
            </p>

            <div className="bg-white rounded-xl border-2 border-black p-3 mb-4 max-h-64 overflow-y-auto" data-testid="export-summary-box">
              {loading && (
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Räknar dina saker...
                </div>
              )}
              {summary && (
                <>
                  <div className="text-sm font-bold mb-2">
                    📦 {summary.total_items} objekt · {summary.collection_count} samlingar
                  </div>
                  <ul className="text-xs space-y-0.5">
                    {summary.collections.slice(0, 20).map((c) => (
                      <li key={c.collection} className="flex justify-between">
                        <span className="text-gray-600">{c.collection}</span>
                        <span className="font-mono">{c.count}</span>
                      </li>
                    ))}
                    {summary.collections.length > 20 && (
                      <li className="text-gray-500 italic">
                        ...och {summary.collections.length - 20} till 💫
                      </li>
                    )}
                  </ul>
                </>
              )}
            </div>

            <p className="text-[11px] text-gray-500 mb-4 italic">
              🔒 Lösenord, push-tokens och tekniska loggar ingår inte — bara din data.
            </p>

            <button
              onClick={handleDownload}
              disabled={downloading || loading}
              className="neo-btn w-full py-3 bg-gradient-to-br from-[#B9FBC0] to-[#7CE8A6] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="export-data-download-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Förbereder JSON...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" /> Ladda ner min data (JSON)
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
